import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Trash2, LogOut, PenLine, Plus, X } from "lucide-react";
import { API, LOGO_URL } from "@/lib/scroll";
import { usePageMeta } from "@/hooks/usePageMeta";

const FILTERS = ["all", "new", "read", "archived"];
const EMPTY_POST = { title: "", tag: "Insights", excerpt: "", read_time: "5 min", content: "", published: true };

const inputCls =
  "w-full bg-[#060B18] border border-amber-500/20 px-4 py-3 text-sm text-amber-50 placeholder:text-slate-500 focus:outline-none focus:border-amber-400/60 transition-colors";

export default function AdminDashboard() {
  usePageMeta("Admin Dashboard | Build Right Partners", "", true);
  const [tab, setTab] = useState("enquiries");
  const [data, setData] = useState({ enquiries: [], stats: { total: 0, new: 0, read: 0, archived: 0 } });
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [postForm, setPostForm] = useState(EMPTY_POST);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const token = localStorage.getItem("brp_token");
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    try {
      const [eq, ps] = await Promise.all([
        axios.get(`${API}/enquiries`, auth),
        axios.get(`${API}/admin/posts`, auth),
      ]);
      setData(eq.data);
      setPosts(ps.data.posts);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("brp_token");
        navigate("/admin");
      } else {
        toast.error("Could not load data.");
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, navigate]);

  useEffect(() => {
    if (!token) {
      navigate("/admin");
      return;
    }
    load();
  }, [token, navigate, load]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/enquiries/${id}`, { status }, auth);
      toast.success(`Marked as ${status}.`);
      load();
    } catch {
      toast.error("Status update failed.");
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/enquiries/${id}`, auth);
      toast.success("Enquiry deleted.");
      load();
    } catch {
      toast.error("Delete failed.");
    }
  };

  const startNew = () => {
    setEditing("new");
    setPostForm(EMPTY_POST);
  };

  const startEdit = (p) => {
    setEditing(p.id);
    setPostForm({ title: p.title, tag: p.tag, excerpt: p.excerpt, read_time: p.read_time || "5 min", content: p.content, published: p.published !== false });
  };

  const savePost = async (e) => {
    e.preventDefault();
    try {
      if (editing === "new") {
        await axios.post(`${API}/posts`, postForm, auth);
        toast.success("Post published.");
      } else {
        await axios.put(`${API}/posts/${editing}`, postForm, auth);
        toast.success("Post updated.");
      }
      setEditing(null);
      load();
    } catch {
      toast.error("Could not save the post.");
    }
  };

  const deletePost = async (id) => {
    try {
      await axios.delete(`${API}/posts/${id}`, auth);
      toast.success("Post deleted.");
      load();
    } catch {
      toast.error("Delete failed.");
    }
  };

  const logout = () => {
    localStorage.removeItem("brp_token");
    navigate("/admin");
  };

  const visible = useMemo(() => {
    const q = search.toLowerCase();
    return data.enquiries.filter(
      (e) =>
        (filter === "all" || e.status === filter) &&
        (!q || [e.name, e.email, e.company, e.service].join(" ").toLowerCase().includes(q))
    );
  }, [data, filter, search]);

  const statCards = [
    { label: "Total Leads", value: data.stats.total },
    { label: "New", value: data.stats.new },
    { label: "Read", value: data.stats.read },
    { label: "Archived", value: data.stats.archived },
  ];

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-[#060B18] text-slate-100">
      <header className="border-b border-amber-500/15 bg-[#0A1128]/70 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="h-9 w-9 rounded-sm overflow-hidden ring-1 ring-amber-400/40">
              <img src={LOGO_URL} alt="" className="h-full w-full object-cover" />
            </span>
            <div className="leading-none">
              <p className="font-serif font-semibold text-amber-50">Enquiry Command</p>
              <p className="font-mono text-[9px] tracking-[0.3em] text-amber-500/70 uppercase">Admin Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-5">
            <Link
              data-testid="admin-back-to-site-link"
              to="/"
              className="hidden sm:block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400 hover:text-amber-300 transition-colors"
            >
              View Site
            </Link>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-amber-300 border border-amber-400/40 rounded-full px-4 py-2 hover:bg-amber-400 hover:text-[#060B18] transition-colors"
            >
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex gap-2 mb-8">
          <button
            data-testid="admin-tab-enquiries"
            onClick={() => setTab("enquiries")}
            className={`rounded-full px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] border transition-colors ${
              tab === "enquiries" ? "bg-amber-400 text-[#060B18] border-amber-400" : "border-amber-500/25 text-slate-300 hover:border-amber-400/60"
            }`}
          >
            Enquiries
          </button>
          <button
            data-testid="admin-tab-posts"
            onClick={() => setTab("posts")}
            className={`rounded-full px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] border transition-colors ${
              tab === "posts" ? "bg-amber-400 text-[#060B18] border-amber-400" : "border-amber-500/25 text-slate-300 hover:border-amber-400/60"
            }`}
          >
            Blog Posts
          </button>
        </div>

        {tab === "enquiries" && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
              {statCards.map((s, i) => (
                <motion.div
                  key={s.label}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.07, duration: 0.5 }}
                  className="bg-[#0A1128]/80 border border-amber-500/15 p-6"
                  data-testid={`admin-stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  <p className="font-serif text-4xl text-amber-300">{s.value}</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">{s.label}</p>
                </motion.div>
              ))}
            </div>

            <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
              <div className="flex gap-2">
                {FILTERS.map((f) => (
                  <button
                    key={f}
                    data-testid={`admin-filter-${f}`}
                    onClick={() => setFilter(f)}
                    className={`rounded-full px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] border transition-colors ${
                      filter === f
                        ? "bg-amber-400 text-[#060B18] border-amber-400"
                        : "border-amber-500/25 text-slate-300 hover:border-amber-400/60"
                    }`}
                  >
                    {f}
                  </button>
                ))}
              </div>
              <input
                data-testid="admin-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, email, company, service…"
                className="md:ml-auto w-full md:w-80 bg-[#0A1128]/80 border border-amber-500/20 px-4 py-2.5 text-sm text-amber-50 placeholder:text-slate-500 focus:outline-none focus:border-amber-400/60"
              />
            </div>

            <div data-testid="admin-enquiry-table" className="space-y-4">
              {loading && <p className="text-slate-400 text-sm font-mono tracking-widest uppercase">Loading…</p>}
              {!loading && visible.length === 0 && (
                <div className="border border-amber-500/15 bg-[#0A1128]/60 p-12 text-center">
                  <p className="font-serif text-2xl text-amber-100 italic">No enquiries yet.</p>
                  <p className="mt-2 text-sm text-slate-400">New leads from the contact form will land here.</p>
                </div>
              )}
              {visible.map((e) => (
                <motion.article
                  key={e.id}
                  layout
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-[#0A1128]/80 border border-amber-500/15 p-6 grid lg:grid-cols-12 gap-5"
                  data-testid={`admin-enquiry-row-${e.id}`}
                >
                  <div className="lg:col-span-3">
                    <p className="font-serif text-xl text-amber-50">{e.name}</p>
                    <p className="text-sm text-slate-400">{e.company || "—"}</p>
                    <p className="mt-2 text-xs text-amber-300/90">{e.email}</p>
                    {e.phone && <p className="text-xs text-slate-400">{e.phone}</p>}
                  </div>
                  <div className="lg:col-span-3">
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-500/70">Service</p>
                    <p className="mt-1 text-sm text-slate-200">{e.service}</p>
                    <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-amber-500/70">Budget</p>
                    <p className="mt-1 text-sm text-slate-200">{e.budget || "—"}</p>
                    <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
                      {e.created_at ? new Date(e.created_at).toLocaleString() : ""}
                    </p>
                  </div>
                  <div className="lg:col-span-4">
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-500/70">Message</p>
                    <p className="mt-1 text-sm leading-relaxed text-slate-300">{e.message}</p>
                  </div>
                  <div className="lg:col-span-2 flex lg:flex-col items-start gap-3">
                    <select
                      data-testid={`admin-enquiry-status-toggle-${e.id}`}
                      value={e.status}
                      onChange={(ev) => setStatus(e.id, ev.target.value)}
                      className="bg-[#060B18] border border-amber-500/25 px-3 py-2 text-xs uppercase tracking-[0.15em] text-amber-200 focus:outline-none focus:border-amber-400/60 appearance-none"
                    >
                      <option value="new">New</option>
                      <option value="read">Read</option>
                      <option value="archived">Archived</option>
                    </select>
                    <button
                      data-testid={`admin-enquiry-delete-button-${e.id}`}
                      onClick={() => remove(e.id)}
                      aria-label="Delete enquiry"
                      className="inline-flex items-center gap-2 text-xs text-red-400/80 hover:text-red-300 border border-red-500/25 hover:border-red-400/50 px-3 py-2 transition-colors"
                    >
                      <Trash2 size={13} /> Delete
                    </button>
                  </div>
                </motion.article>
              ))}
            </div>
          </>
        )}

        {tab === "posts" && (
          <div data-testid="admin-posts-section">
            {editing === null ? (
              <>
                <button
                  data-testid="admin-post-new-button"
                  onClick={startNew}
                  className="mb-8 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#F3E5AB] via-[#D4AF37] to-[#AA7C11] px-6 py-3 font-mono text-xs uppercase tracking-[0.2em] font-semibold text-[#060B18] hover:shadow-[0_0_35px_rgba(212,175,55,0.35)] transition-shadow"
                >
                  <Plus size={14} /> New Post
                </button>
                <div className="space-y-4">
                  {posts.length === 0 && !loading && (
                    <div className="border border-amber-500/15 bg-[#0A1128]/60 p-12 text-center">
                      <p className="font-serif text-2xl text-amber-100 italic">No posts yet.</p>
                      <p className="mt-2 text-sm text-slate-400">Publish your first insight above.</p>
                    </div>
                  )}
                  {posts.map((p) => (
                    <div
                      key={p.id}
                      data-testid={`admin-post-row-${p.id}`}
                      className="bg-[#0A1128]/80 border border-amber-500/15 p-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between"
                    >
                      <div>
                        <p className="font-serif text-xl text-amber-50">{p.title}</p>
                        <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">
                          {p.tag} · {p.created_at ? new Date(p.created_at).toLocaleDateString() : ""} · {p.published !== false ? "Published" : "Draft"}
                        </p>
                      </div>
                      <div className="flex gap-3 shrink-0">
                        <button
                          data-testid={`admin-post-edit-button-${p.id}`}
                          onClick={() => startEdit(p)}
                          className="inline-flex items-center gap-2 text-xs text-amber-300 border border-amber-400/30 hover:border-amber-300 px-3 py-2 transition-colors"
                        >
                          <PenLine size={13} /> Edit
                        </button>
                        <button
                          data-testid={`admin-post-delete-button-${p.id}`}
                          onClick={() => deletePost(p.id)}
                          className="inline-flex items-center gap-2 text-xs text-red-400/80 hover:text-red-300 border border-red-500/25 hover:border-red-400/50 px-3 py-2 transition-colors"
                        >
                          <Trash2 size={13} /> Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <form
                data-testid="admin-post-form"
                onSubmit={savePost}
                className="max-w-3xl bg-[#0A1128]/80 border border-amber-500/20 p-8 space-y-5"
              >
                <div className="flex items-center justify-between">
                  <h2 className="font-serif text-2xl font-semibold text-amber-50">
                    {editing === "new" ? "New Post" : "Edit Post"}
                  </h2>
                  <button
                    type="button"
                    data-testid="admin-post-cancel-button"
                    onClick={() => setEditing(null)}
                    className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400 hover:text-amber-300 transition-colors"
                  >
                    <X size={14} /> Cancel
                  </button>
                </div>
                <input
                  data-testid="admin-post-title-input"
                  required
                  value={postForm.title}
                  onChange={(e) => setPostForm((f) => ({ ...f, title: e.target.value }))}
                  placeholder="Post title *"
                  className={inputCls}
                />
                <div className="grid sm:grid-cols-2 gap-5">
                  <input
                    data-testid="admin-post-tag-input"
                    value={postForm.tag}
                    onChange={(e) => setPostForm((f) => ({ ...f, tag: e.target.value }))}
                    placeholder="Tag (e.g. Paid Media)"
                    className={inputCls}
                  />
                  <input
                    data-testid="admin-post-readtime-input"
                    value={postForm.read_time}
                    onChange={(e) => setPostForm((f) => ({ ...f, read_time: e.target.value }))}
                    placeholder="Read time (e.g. 6 min)"
                    className={inputCls}
                  />
                </div>
                <textarea
                  data-testid="admin-post-excerpt-input"
                  required
                  rows={2}
                  value={postForm.excerpt}
                  onChange={(e) => setPostForm((f) => ({ ...f, excerpt: e.target.value }))}
                  placeholder="Excerpt — the teaser shown on the blog grid *"
                  className={`${inputCls} resize-none`}
                />
                <textarea
                  data-testid="admin-post-content-textarea"
                  required
                  rows={10}
                  value={postForm.content}
                  onChange={(e) => setPostForm((f) => ({ ...f, content: e.target.value }))}
                  placeholder="Full post content. Separate paragraphs with a blank line. *"
                  className={`${inputCls} resize-y`}
                />
                <button
                  data-testid="admin-post-save-button"
                  type="submit"
                  className="rounded-full bg-gradient-to-r from-[#F3E5AB] via-[#D4AF37] to-[#AA7C11] px-8 py-3.5 font-mono text-xs uppercase tracking-[0.25em] font-semibold text-[#060B18] hover:shadow-[0_0_35px_rgba(212,175,55,0.35)] transition-shadow"
                >
                  {editing === "new" ? "Publish Post" : "Save Changes"}
                </button>
              </form>
            )}
          </div>
        )}
      </div>
    </main>
  );
}
